import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { memo } from 'react';
import styled, { css } from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import type { IIconButtonProps } from './IconButton.types';

const IconButton = ({
	className,
	icon,
	disabled,
	buttonType = 'button',
	...props
}: IIconButtonProps) => {
	const isIconPredefined = typeof icon === 'string';
	const IconComponent = isIconPredefined ? (
		<IconWrapper>
			<Icon variant={icon as PredefinedIcons} />
		</IconWrapper>
	) : (
		<IconWrapper>{icon}</IconWrapper>
	);

	return (
		<button
			type={buttonType}
			className={`${className} ${!disabled ? 'clickAnimation' : ''}`}
			disabled={disabled}
			{...props}
		>
			{IconComponent}
		</button>
	);
};

const IconWrapper = styled.div`
	display: contents;
`;

const Memoized = memo(IconButton);
const Styled = styled(Memoized)`
	--color: ${({ theme }) => theme.v2.color.neutral[600]};
	--background: ${({ theme }) => theme.v2.color.white[500]};
	--padding-size: 0.5rem;

	display: flex;
	justify-content: center;
	align-items: center;
	height: fit-content;
	flex-shrink: 0;
	background-color: var(--background);
	border: 1px solid var(--color);
	padding: var(--padding-size);
	border-radius: 4px;

	${Icon} {
		--size: 0.9rem;
		&.svg {
			color: var(--color);
			fill: var(--color);
		}
	}

	${({ theme, active }) =>
		active
			? `
        --color: ${theme.v2.color.white[500]};
        --background: ${theme.v2.color.secondary[800]};
    `
			: ``}

	${({ variant, theme }) => {
		switch (variant) {
			case 'filled': {
				return `
                    background-color: ${theme.v2.color.neutral[200]};
                    border: none;
                `;
			}
			default:
				return ``;
		}
	}}

    ${({ size }) => {
		switch (size) {
			case 'md': {
				return `
                --padding-size: 0.5rem;
                `;
			}
			default:
				return `
                --padding-size: 0.8rem;

                `;
		}
	}}

    ${({ round }) => round && 'border-radius: 100%'};
	${({ disabled }) =>
		disabled
			? css`
					cursor: not-allowed;
				`
			: css`
					&:hover {
						--color: ${({ theme }) => theme.v2.color.secondary[800]};
						--background: ${({ theme }) => theme.v2.color.secondary[200]};
					}
				`}
`;

export { Styled as IconButton };
